/**
 * Documents (Writing's pages, written like in Word): each a file among the project's assets
 * (`Pages/<folder>/<Name>.page.json`) kept by the core's `pages` module, and the document of
 * one node (its first attachment that is a page). The service mirrors what every page says
 * (`pages.list`: title, template, parameters, Markdown; read again on `pages.changed` and
 * `assets.changed`), the templates (`pages.templates`), and reads, saves, makes and converts
 * pages. Two people saving one page are merged by the core block by block; the editor shows
 * what comes back.
 *
 * `schema.ts` is what a document may hold, `convert.ts` turns pages into the editor's
 * documents and back, `paginate.ts` lays them out on sheets of paper.
 */
import { createMemo, createSignal, type Accessor } from "solid-js";
import type { Node } from "@protocol/Node";
import type { Page } from "@protocol/Page";
import type { PageSummary } from "@protocol/PageSummary";
import type { TemplateDef } from "@protocol/TemplateDef";
import { isClientError, type Client } from "../../client/client";
import type { GraphMirror } from "../../client/graph";
import { toast } from "../../shared/toast";
import type { Environments } from "../environments";

export * from "./constants";
export * from "./convert";
export * from "./paginate";
export * from "./pictures";
export * from "./schema";

/** The ending of a page's file name. */
export const PAGE_EXTENSION = ".page.json";

/** The document of `node`: the first of its attachments that is a page (`null`: none). */
export function documentOf(node: Pick<Node, "attachments">): string | null {
  return node.attachments?.find((p) => p.endsWith(PAGE_EXTENSION)) ?? null;
}

/** The template a node of kind `kind` is written with: one making that kind, else the note's, else the first. */
export function templateFor(templates: readonly TemplateDef[], kind: string): TemplateDef | null {
  return templates.find((t) => t.kind === kind) ?? templates.find((t) => t.id === "note") ?? templates[0] ?? null;
}

/** What a save answered: the page kept (merged with a save made since), and the blocks both people changed. */
export interface Saved {
  /** The page as the core keeps it now. */
  page: Page;
  /** The blocks both changed, by id (theirs follows each, marked). */
  conflicts: string[];
}

/** The documents service. */
export interface Documents {
  /** What every page of the project says, by path. */
  index: Accessor<ReadonlyMap<string, PageSummary>>;
  /** The templates, the environments' first, then the project's. */
  templates: Accessor<TemplateDef[]>;
  /** The template `id`, if any (the project's own winning). */
  template(id: string | null | undefined): TemplateDef | null;
  /** What node `node`'s document says, as Markdown (`null`: it has none, or it is not read yet). */
  textOf(node: Pick<Node, "attachments">): string | null;
  /** Nodes of the graph shown whose old Markdown page is not a document yet. */
  unconverted: Accessor<Node[]>;
  /** Reads page `path` (`null`: refused, said in a toast). */
  read(path: string): Promise<Page | null>;
  /** Saves `page` at `path`, `base` being the page as it was read (`null`: refused, said in a toast). */
  save(path: string, base: Page | null, page: Page): Promise<Saved | null>;
  /** Makes a page from template `template` named `name`, and its node (or gives it to node `node`). */
  create(template: string, name: string, node?: string): Promise<{ path: string; node: string } | null>;
  /** Keeps a template among the project's files. */
  saveTemplate(template: TemplateDef): Promise<boolean>;
  /** Turns the environment's old Markdown pages into documents (one undo); how many. */
  convert(): Promise<number>;
  /** Called when a page is saved or made, by anyone (`by`: who). Returns the remover. */
  onChanged(handler: (path: string, by: string | null) => void): () => void;
}

/** What the service is built from. */
export interface DocumentsDeps {
  client: Client;
  /** The graph shown (its nodes' documents and old pages). */
  graph: Pick<GraphMirror, "state">;
  /** Where new pages' nodes go. */
  environments: Pick<Environments, "current" | "workspace">;
}

/** Starts the documents service. */
export function createDocuments({ client, graph, environments }: DocumentsDeps): Documents {
  const [index, setIndex] = createSignal<ReadonlyMap<string, PageSummary>>(new Map());
  const [templates, setTemplates] = createSignal<TemplateDef[]>([]);
  const handlers = new Set<(path: string, by: string | null) => void>();
  let listing: Promise<void> | null = null;
  let again = false;

  /** Reads the list of pages again (one at a time; a change while reading reads once more). */
  const list = () => {
    if (listing) {
      again = true;
      return;
    }
    listing = (async () => {
      do {
        again = false;
        try {
          const response = await client.request({ method: "pages.list" });
          if (response.type === "pages") setIndex(new Map(response.pages.map((p) => [p.path, p])));
        } catch {
          // No pages module (an older core) or not connected: nothing listed.
        }
      } while (again);
      listing = null;
    })();
  };
  const loadTemplates = async () => {
    try {
      const response = await client.request({ method: "pages.templates" });
      if (response.type === "templates") setTemplates(response.templates);
    } catch {
      setTemplates([]);
    }
  };
  client.onOpen(() => {
    list();
    void loadTemplates();
  });
  client.onEvent((event) => {
    if (event.event === "pages.changed") {
      list();
      for (const handler of handlers) handler(event.data.path, event.data.by ?? null);
    } else if (event.event === "assets.changed" && event.data.source === "project") {
      list();
      void loadTemplates();
    } else if (event.event === "plugins.changed" || event.event === "project.changed") {
      list();
      void loadTemplates();
    }
  });

  /** Sends a request, a refusal said in a toast; gives the answer or `null`. */
  const send = async (request: Parameters<Client["request"]>[0]) => {
    try {
      return await client.request(request);
    } catch (error) {
      toast(isClientError(error) ? error.message : String(error), "error");
      return null;
    }
  };

  const unconverted = createMemo(() => graph.state.graph.nodes.filter((n) => (n.page ?? "").trim() && !documentOf(n)));

  return {
    index,
    templates,
    template: (id) => (id ? ([...templates()].reverse().find((t) => t.id === id) ?? null) : null),
    textOf(node) {
      const path = documentOf(node);
      return path ? (index().get(path)?.markdown ?? null) : null;
    },
    unconverted,
    async read(path) {
      const response = await send({ method: "pages.read", params: { path } });
      return response?.type === "page" ? response.page : null;
    },
    async save(path, base, page) {
      const response = await send({ method: "pages.save", params: { path, base, page } });
      return response?.type === "page" ? { page: response.page, conflicts: response.conflicts ?? [] } : null;
    },
    async create(template, name, node) {
      const environment = environments.current()?.id ?? null;
      const workspace = environments.workspace()?.id ?? null;
      const response = await send({ method: "pages.create", params: { template, name, folder: "", node: node ?? null, environment, workspace, group: null } });
      if (response?.type !== "page" || !response.node) return null;
      list();
      return { path: response.path, node: response.node };
    },
    async saveTemplate(template) {
      const response = await send({ method: "pages.save_template", params: { template } });
      if (response?.type === "templates") setTemplates(response.templates);
      return response?.type === "templates";
    },
    async convert() {
      const response = await send({ method: "pages.convert", params: { environment: environments.current()?.id ?? null } });
      if (response?.type !== "converted") return 0;
      list();
      return response.paths.length;
    },
    onChanged(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
  };
}
